import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../lib/api';
import GateReportPanel from './GateReportPanel';

const PHASE_NAMES: Record<number, string> = {
  1: 'Requirements',
  2: 'Architecture',
  25: 'UX Design',
  3: 'Sprint Planning',
  4: 'Development',
  5: 'Code Review',
  6: 'Testing',
  7: 'Deployment',
  8: 'Retrospective',
};

// For EXIT gates the URL phaseNumber is the source phase being exited.
// Fractional phases (e.g. 2.5) break the simple `toPhaseNumber - 1` shortcut,
// so we map fromPhase explicitly. Mirrors PHASE_MAP in routes/phase-transitions.ts.
const FROM_PHASE_TO_URL_NUMBER: Record<string, number> = {
  phase_1_requirements:    1,
  phase_2_architecture:    2,
  phase_2_5_ux_design:    25,
  phase_3_sprint_planning: 3,
  phase_4_development:     4,
  phase_5_code_review:     5,
  phase_6_testing:         6,
  phase_7_deployment:      7,
  phase_8_retrospective:   8,
};

interface GateItem {
  title: string;
  status: 'PASS' | 'FAIL' | 'PENDING' | 'WAIVED'; // FT-GATE.A2 — sync with API GateItem.status
  reason?: string;
  actionRequired?: string;
}

interface TransitionSuccess {
  gateResult: {
    phase: string;
    gateType: string;
    overallResult: string;
    items: GateItem[];
    evaluatedAt: string;
  };
  phaseState: unknown;
}

interface TransitionFailure {
  error: string;
  gateResult: {
    overallResult: string;
    items: GateItem[];
  };
  canOverride: boolean;
}

export interface PhaseTransitionModalProps {
  projectId: string;
  fromPhase: string;
  toPhaseNumber: number;
  direction: 'ENTRY' | 'EXIT';
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function PhaseTransitionModal({
  projectId,
  fromPhase,
  toPhaseNumber,
  direction,
  isOpen,
  onClose,
  onSuccess,
}: PhaseTransitionModalProps) {
  const [state, setState] = useState<'loading' | 'pass' | 'fail' | 'error'>('loading');
  const [failResult, setFailResult] = useState<TransitionFailure | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  const phaseName = PHASE_NAMES[toPhaseNumber] ?? `Phase ${toPhaseNumber}`;
  // For EXIT gates the URL phaseNumber is the source phase being exited,
  // not the destination. fromPhase maps cleanly even for fractional phases
  // like 2.5; toPhaseNumber - 1 only works for adjacent integer phases.
  const apiPhaseNumber = direction === 'EXIT'
    ? (FROM_PHASE_TO_URL_NUMBER[fromPhase] ?? toPhaseNumber - 1)
    : toPhaseNumber;

  const runTransition = useCallback(() => {
    setState('loading');
    setFailResult(null);
    setErrorMsg('');

    apiFetch<TransitionSuccess>(`/projects/${projectId}/phases/${apiPhaseNumber}/transition`, {
      method: 'POST',
      body: JSON.stringify({ direction }),
    })
      .then(() => {
        setState('pass');
        setTimeout(() => onSuccessRef.current(), 1200);
      })
      .catch(async (err) => {
        if (err && typeof err === 'object' && 'gateResult' in err) {
          setState('fail');
          setFailResult(err as TransitionFailure);
        } else if (err instanceof Error && err.message.includes('gate failed')) {
          setState('fail');
          try {
            const parsed = JSON.parse(err.message);
            setFailResult(parsed);
          } catch {
            setErrorMsg(err.message);
            setState('error');
          }
        } else {
          setErrorMsg(err instanceof Error ? err.message : 'Transition failed');
          setState('error');
        }
      });
  }, [projectId, apiPhaseNumber, direction]);

  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => runTransition(), 0);
    return () => clearTimeout(id);
  }, [isOpen, runTransition]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          width: '100%', maxWidth: 560,
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: 28 }}>
        {/* Loading state */}
        {state === 'loading' && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '32px 0', gap: 12,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              border: '3px solid var(--border)', borderTopColor: 'var(--primary)',
              animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
              Running pre-flight checks...
            </div>
          </div>
        )}

        {/* Pass state */}
        {state === 'pass' && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '32px 0', gap: 12,
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: 'rgba(16,185,129,0.12)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: 'var(--success)', fontSize: 24, fontWeight: 700,
            }}>
              ✓
            </div>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
              color: 'var(--text)',
            }}>
              All checks passed
            </div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)',
            }}>
              Transitioning to {phaseName}...
            </div>
          </div>
        )}

        {/* Fail state — embed gate report */}
        {state === 'fail' && failResult && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
                color: 'var(--text)', marginBottom: 4,
              }}>
                Pre-Flight Check Failed
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                The following checks must pass before transitioning to {phaseName}.
              </div>
            </div>

            <GateReportPanel
              projectId={projectId}
              phaseNumber={apiPhaseNumber}
              gateType={direction === 'ENTRY' ? 'entry' : 'exit'}
              isOpen={true}
              onClose={() => {}}
              canOverride={failResult.canOverride}
              onOverrideSuccess={() => {
                onClose();
                onSuccess();
              }}
              inline
            />
          </>
        )}

        {/* Error state */}
        {state === 'error' && (
          <div style={{ padding: '16px 0' }}>
            <div style={{
              padding: '12px 14px', borderRadius: 'var(--radius-sm)',
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
              color: '#ef4444', fontSize: 13,
            }}>
              {errorMsg}
            </div>
          </div>
        )}
        </div>

        {/* Footer — always visible (fail + error states) */}
        {(state === 'fail' || state === 'error') && (
          <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', padding: '16px 28px 28px', borderTop: '1px solid var(--border)' }}>
            <button
              onClick={onClose}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
